import { useState, useEffect, useRef, useContext, useCallback } from 'react';
// store
import { useSelector, useDispatch, SET_DEVICE, SET_RANGES_ANALYSIS } from '../../store';
// hooks
import useResponsive from '../../hooks/useResponsive';
// components
import Breadcrumbs from '../../components/breadcrumbs';
import Dialog from '../../components/dialog';
import { Label, TextFields, Message as FormMessage } from 'components/hook-form';
// @devextreme
import DateBox from 'devextreme-react/date-box';
import DateRangeBox from 'devextreme-react/date-range-box';
import { Template } from 'devextreme-react/core/template';
import { Popover } from 'devextreme-react/popover';
import Chart, { Series, CommonSeriesSettings, Point, Legend, Tooltip, Crosshair, HorizontalLine, ValueAxis, Pane, ConstantLine, VisualRange, ArgumentAxis, Label as DxLabel } from 'devextreme-react/chart';
import RangeSelector, { Size, Chart as RsChart, Series as RsSeries, Behavior, RangeSelectorTypes, Label as RsLabel } from 'devextreme-react/range-selector';
// @moment
import moment from 'moment-timezone';
// utils
import { timezones } from '../../utils/format';
// assets
import { ReactComponent as Download } from '../../assets/button/download.svg';
import { ReactComponent as Lines } from '../../assets/button/lines.svg';
import { ReactComponent as Settings } from '../../assets/button/settings.svg';
import Calendar from '../../assets/button/Calendar';
// styles
import { ThemeContext } from 'styled-components';
import { ListContainer, SearchWrapper, Tabs, Box, PrimaryButton, InfoMessage, AnalysisPopover, IconButton, SelectButton, DateButton, MobileDateWrapper, RangesPopover, TextSwitch } from '../../styles';
// apis
import { getAnalysis, getDetailAnalysis } from '../../apis';

// ----------------------------------------------------------------------

const now = timezones(moment().utc().format(), 'YYYY-MM-DDTHH:mm:ss');
const daysAgo = timezones(moment().utc().subtract(30, 'days').format(), 'YYYY-MM-DDTHH:mm:ss');
const intervals = [{ value: 'daily', name: '일간 평균' }, { value: 'weekly', name: '주간 평균' }, { value: 'monthly', name: '월간 평균' }, { value: 'setting', name: '직접 설정' }];

const colors = ['blue', 'green', 'yellow', 'purple', 'pink', 'error'];

// ----------------------------------------------------------------------

export default function AnalysisPage() {
  const theme = useContext(ThemeContext);
  const dispatch = useDispatch();

  const ranges = useSelector((state) => state.rangesAnalysis);

  const [ visibleRanges, setVisibleRanges ] = useState(false);
  const [ axisRange, setAxisRange ] = useState(ranges);
  const [ formMessage, setFormMessage ] = useState(false);

  useEffect(() => {
    setAxisRange(ranges);
  }, [ranges]);
  
  const handleRanges = () => {
    const faild = Object.values(axisRange).find((item: any) => { return item.manual && ( item.startValue > item.endValue ) });
    if ( faild ) {
      return setFormMessage(true);
    } else {
      dispatch( SET_RANGES_ANALYSIS(axisRange));
      setVisibleRanges(false);
      setFormMessage(false);
    }
  };

  const group = useSelector((state) => state.group);

  const device = useSelector((state) => state.device);
  const devices = useSelector((state) => state.devices);

  const mobile = useResponsive('down', 'md');

  const [ chart, setChart ] = useState('bar');

  const [ active, setActive ] = useState('daily');
  const [ interval, setInterval ] = useState('daily');
  const [ display, setDisplay ] = useState('yyyy-MM-dd');

  const [ saveStart, setSaveStart ] = useState<any>({ daily: new Date(now), weekly: new Date(now), monthly: new Date(now) });
  const [ saveEnd, setSaveEnd ] = useState<any>({ daily: new Date(now), weekly: new Date(now), monthly: new Date(now) });

  const [ start, setStart ] = useState(new Date(now));
  const [ end, setEnd ] = useState(new Date(now));
  const [ range, setRange ] = useState(false);

  const [ setting, setSetting ] = useState(false);
  const [ settingDate, setSettingDate ] = useState({ start: new Date(daysAgo), end: end});

  const [ detailed, setDetailed ] = useState(false);
  const [ detailedDate, setDetailedDate ] = useState(start);

  const [ data, setData ] = useState<{
    name: [], units: {[key: string]: string}, datasets: [], alarm: {[key: string]: any}, dataArr: {[key: string]: any} }>({ name: [], units: {}, datasets: [], alarm: {}, dataArr: []
  });

  const [ lines, setLines ] = useState(false);

  const [ message, setMessage ] = useState(null);
  const handleClose = () => setMessage(null);

  const [ visibleDevice, setVisibleDevice ] = useState(false);

  const [visualRange, setVisualRange] = useState({});

  const updateVisualRange = useCallback((event: RangeSelectorTypes.ValueChangedEvent) => {
    setVisualRange(event.value);
  }, [setVisualRange]);

  useEffect(() => {
    if ( device?.iotDeviceId ) handleSearch();
    else setData({ name: [], units: {}, datasets: [], alarm: {}, dataArr: []});
  }, [device]);

  const handleSelection = (value: any) => {
    dispatch( SET_DEVICE(value) );
    setVisibleDevice(false);
  };

  useEffect(() => {
    setSaveStart({ ...saveStart, [interval]: start});
  }, [start]);

  useEffect(() => {
    setSaveEnd({ ...saveEnd, [interval]: end});
  }, [end]);

  useEffect(() => {
    if ( interval === 'monthly' ) {
      document.body.className  += ' active';
    } else {
      document.body.classList.remove('active');
    }

    if ( interval === 'weekly' ) {
      handleWeekly(saveStart.weekly);
    } else {
      setStart(saveStart[interval]);
      setEnd(saveEnd[interval]);
    }

    if ( interval === 'monthly' ) return setDisplay('yyyy-MM');
    setDisplay('yyyy-MM-dd');
  }, [interval]);

  const handleWeekly = (value: Date) => {
    const current = new Date(value);
    const startDate = new Date(current.setDate(current.getDate() - current.getDay()));
    const sunday = new Date(startDate);
    let endDate = new Date(sunday.setDate(sunday.getDate() + 6));
    if ( endDate.getTime() > new Date(now).getTime() ) endDate = new Date(now);

    setStart(new Date(startDate.getTime()));
    setEnd(new Date(endDate.getTime()));
  };

  const onValueChange = (event: any) => {
    if ( !mobile ) {
      const previous = event.previousValue;
      const values = event.value;
      const filter = values.filter((value: string) => !previous.includes(value));
      const value = filter.length > 0 ? filter[0] : null;
  
      const startDate = new Date(previous[0]).getTime();
      const endDate = new Date(previous[1]).getTime();
      const currentDate = new Date(value).getTime();
  
      if ( !value || ( currentDate >= startDate && currentDate <= endDate ) ) return;
  
      setRange(false);
      handleWeekly(value);
      document.getElementById('rangeStart').blur();
      setTimeout(() => {
        document.getElementById('rangeEnd').blur();
      }, 5);
    } else {
      handleWeekly(event.value);
    }
  };

  const handleSearch = () => {
    let search = {interval, start, end: (interval === 'daily' || interval === 'monthly') ? start : end};

    if ( active === 'setting' ) {
      if ( detailed ) {
        return getDetailAnalysis(device.iotDeviceId, detailedDate)
          .then((response) => {
            if ( response.waterQualities.length > 0 ) {
              const info = [ ...device.waterQualitySensors ];
              const units: {[key: string]: string} = {};
              const alarm: {[key: string]: {}} = {};
              const dataset: any = [];
              const dataArr: {[key: string]: any} = {};

              response.waterQualities.forEach((element: any) => {
                const filter = info.find((item: any) => item.waterQualitySensorId === element.waterQualitySensorId);
                units[filter.name] = filter.unit;
                alarm[filter.name] = {alarmType: filter.alarmType, alarmLowerLimit: filter.alarmLowerLimit, alarmUpperLimit: filter.alarmUpperLimit};
                dataArr[filter.name] = [];
                element.detailData.forEach((value: any) => {
                  dataset.push({ ...value, [filter.name]: value.measuredValue, dateTime: value.acquisitionAt});
                  dataArr[filter.name].push(value.measuredValue);
                });
              });
              dataArr.waterTemperature = [];
              response.waterTemperatures.forEach((value: any) => {
                dataset.push({ ...value, waterTemperature: value.waterTemperature, dateTime: value.acquisitionAt});
                dataArr.waterTemperature.push(value.waterTemperature);
              });
              units.waterTemperature = group.waterTemperatureUnit;
              alarm.waterTemperature = {alarmType: 'OFF', alarmLowerLimit: null, alarmUpperLimit: null};

              if ( response.waterQualityPredictions?.length > 0 ) {
                response.waterQualityPredictions.forEach((element: any) => {
                  const filter = device.waterQualityPredictions.find((item: any) => item.waterQualityPredictionId === element.waterQualityPredictionId);
                  units[filter.name] = filter.unit;
                  alarm[filter.name] = {alarmType: filter.alarmType, alarmLowerLimit: filter.alarmLowerLimit, alarmUpperLimit: filter.alarmUpperLimit};
                  dataArr[filter.name] = [];
                  element.detailData.forEach((value: any) => {
                    dataset.push({ ...value, [filter.name]: value.predictionValue, dateTime: value.acquisitionAt});
                    dataArr[filter.name].push(value.predictionValue);
                  });
                });
              }

              const sort: any = info.sort((a: any, b: any) => a.channel - b.channel);
              const name = sort.map((element: any) => element.name);
              name.push('waterTemperature');
              response.waterQualityPredictions.forEach((element: any) => {
                const filter = device.waterQualityPredictions.find((item: any) => item.waterQualityPredictionId === element.waterQualityPredictionId);
                name.push(filter.name);
              });
              setData({ name, units, datasets: dataset, alarm, dataArr });
            } else {
              setData({ name: [], units: {}, datasets: [], alarm: {}, dataArr: {} });
            }
            setChart('line');
          });
      }

      let difference = Math.abs( settingDate.start?.getTime() - settingDate.end?.getTime() );
      difference = Math.ceil( difference / (1000 * 60 * 60 * 24) );
      if ( difference > 30 ) return setMessage('31일 이내로 검색해주세요.');

      search = {interval: 'weekly', start: settingDate.start, end: settingDate.end};
    }
    setDetailed(false);
    if ( moment(search.end).isAfter(now, 'day') ) return;
    if ( !search.start || !search.end ) return;
    if ( search.start > search.end ) return setMessage('검색 시작 시간은 종료 시간보다 이전이어야 합니다.\n검색 기간을 다시 한번 확인해 주세요.');

    getAnalysis(device.iotDeviceId, search)
      .then((response) => {
        if ( response.waterQualities?.length > 0 ) {
          const info = [ ...device.waterQualitySensors ];
          const units: {[key: string]: string} = {};
          const alarm: {[key: string]: {}} = {};
          const dataset: any = [];
          const dataArr: {[key: string]: any} = {};
          response.waterQualities.forEach((element: any) => {
            const filter = info.find((item: any) => item.waterQualitySensorId === element.waterQualitySensorId);
            units[filter.name] = filter.unit;
            alarm[filter.name] = {alarmType: filter.alarmType, alarmLowerLimit: filter.alarmLowerLimit, alarmUpperLimit: filter.alarmUpperLimit};
            dataArr[filter.name] = [];
            element.averageData.forEach((value: any) => {
              dataset.push({ ...value, [filter.name]: value.averageValue, dateTime: timezones(value.dateTime, 'YYYY-MM-DD HH:mm:ss' )});
              dataArr[filter.name].push(value.averageValue);
            });
          })
          dataArr.waterTemperature = [];
          response.waterTemperatures.forEach((value: any) => {
            dataset.push({ ...value, waterTemperature: value.averageValue, dateTime: timezones(value.dateTime, 'YYYY-MM-DD HH:mm:ss' )});
            dataArr.waterTemperature.push(value.averageValue);
          });
          units.waterTemperature = group.waterTemperatureUnit;
          alarm.waterTemperature = {alarmType: 'OFF', alarmLowerLimit: null, alarmUpperLimit: null};

          if ( response.waterQualityPredictions?.length > 0 ) {
            response.waterQualityPredictions.forEach((element: any) => {
              const filter = device.waterQualityPredictions.find((item: any) => item.waterQualityPredictionId === element.waterQualityPredictionId);
              units[filter.name] = filter.unit;
              alarm[filter.name] = {alarmType: filter.alarmType, alarmLowerLimit: filter.alarmLowerLimit, alarmUpperLimit: filter.alarmUpperLimit};
              dataArr[filter.name] = [];
              element.averageData.forEach((value: any) => {
                dataset.push({ ...value, [filter.name]: value.averageValue, dateTime: timezones(value.dateTime, 'YYYY-MM-DD HH:mm:ss' )});
                dataArr[filter.name].push(value.averageValue);
              });
            });
          }

          const sort: any = info.sort((a: any, b: any) => a.channel - b.channel);
          const name = sort.map((element: any) => element.name);
          name.push('waterTemperature');
          response.waterQualityPredictions.forEach((element: any) => {
            const filter = device.waterQualityPredictions.find((item: any) => item.waterQualityPredictionId === element.waterQualityPredictionId);
            name.push(filter.name);
          });

          setData({ name, units, datasets: dataset, alarm, dataArr });
        } else {
          setData({ name: [], units: {}, datasets: [], alarm: {}, dataArr: {} });
        }
        setChart('bar');
      });
  };

  const handleDate = (type: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    switch ( interval ) {
      case 'daily':
        if ( type !== 'previous' && moment(startDate).isSameOrAfter(now, 'day') ) return;
        const date = type === 'previous' ? startDate.setDate(startDate.getDate() - 1) : startDate.setDate(startDate.getDate() + 1);
        return setStart(new Date(date));
      case 'weekly':
        const date2 = type === 'previous' ? startDate.setDate(startDate.getDate() - 1) : endDate.setDate(endDate.getDate() + 1);
        return handleWeekly(new Date(date2));
      default:
        if ( type !== 'previous' && moment(startDate).isSameOrAfter(now, 'month') ) return;
        const date3 = type === 'previous' ? startDate.setMonth(startDate.getMonth() - 1) : startDate.setMonth(startDate.getMonth() + 1);
        setStart(new Date(date3));
    }
  };

  const customizeTooltip = (point: any) => {
    const value: {[key: string]: any} = {};
    point.points.forEach((element: any) => {
      value[element.seriesName] = element.value;
    });
    return (
      <div>
        <p style={{'fontSize': '11px', 'color': '#C3CAD9'}}>{ timezones(point.argument, detailed ? 'YYYY-MM-DD HH:mm:ss' : interval === 'daily' ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD' ) }</p>
        <div style={{'display': 'flex', 'alignItems': 'center', 'justifyContent': 'space-between'}}>
          <div style={{'fontSize': '12px', 'color': '#F2F3F5', 'marginRight': '8px'}}>
            { data.name.map((name) => (
              (value[name] !== null || value[name] !== undefined) &&
              <p key={name} style={{'lineHeight': '20px'}}>{ name === 'waterTemperature' ? group.waterTemperature : name }</p> 
            ))}
          </div>
          <div style={{'fontSize': '12px', 'color': '#FFFFFF'}}>
            { data.name.map((name) => (
              (value[name] !== null || value[name] !== undefined) &&
              <p key={name} style={{'lineHeight': '20px'}}><span style={{'fontSize': '13px', 'fontWeight': '600'}}>{ value[name] }</span> { data.units[name] }</p> 
            ))}
          </div>
        </div>
      </div>
    );
  };

  const customizeText = (value: any) => {
    return timezones(value, detailed ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD HH:mm' );
  };

  const customizeText2 = (value: any) => {
    return timezones(value, detailed ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD HH:mm' );
  };

  const chartRef = useRef(null);
  const handleDownload = () => {
    chartRef.current.instance.exportTo(`chart-${timezones(Date.now(), 'YYYYMMDDHHmmss')}`, 'png');
  };

  return (
    <ListContainer>
      <Breadcrumbs heading='통계 분석' />

      <SearchWrapper>
        <div>
          { mobile &&
            <SelectButton type='button' id='device' onClick={() => setVisibleDevice(true)} disabled={!device ? true : false}>{ device?.location }<div/></SelectButton>
          }
          <Popover
            target='#device'
            visible={visibleDevice}
            hideOnOutsideClick={true}
            onHiding={() => setVisibleDevice(false)}
            wrapperAttr={{'class': 'mobilePopover'}}
          >
            { devices.map((item: any) => (
              <button type='button' key={item.iotDeviceId} onClick={() => handleSelection(item)}>
                { item.location }
              </button>
            ))}
          </Popover>

          <Tabs>
            { intervals.map((item) => (
              <button
                key={item.value}
                id={item.value}
                className={active === item.value ? 'active' : ''}
                onClick={() => {
                  if ( item.value === 'setting' ) {
                    setSetting(true);
                  } else {
                    setInterval(item.value);
                  }
                  setActive(item.value);
                }}
              >
                {item.name}
              </button>
            ))}
          </Tabs>

          <MobileDateWrapper>
            { interval !== 'setting' &&
              <DateButton onClick={() => handleDate('previous')} disabled={active === 'setting' || !device?.iotDeviceId ? true : false} />
            }
            { interval === 'weekly' ? (
              !mobile ? (
                <DateRangeBox
                  opened={range}
                  value={[start, end]}
                  onValueChanged={onValueChange}
                  onFocusIn={() => setRange(true)}
                  onFocusOut={() => setRange(false)}
                  displayFormat={display}
                  openOnFieldClick={true}
                  useMaskBehavior={true}
                  startDateLabel=''
                  endDateLabel=''
                  startDateInputAttr={{id: 'rangeStart'}}
                  endDateInputAttr={{id: 'rangeEnd'}}
                  disabled={active === 'setting' ? true : false}
                  max={new Date(now)}
                  dropDownButtonTemplate='calendar'
                >
                  <Template name='calendar' render={Calendar} />
                </DateRangeBox>
              ) : (
                <div className={`rangeBox${ active === 'setting' ? ' disabled' : ''}`}>
                  <DateBox
                    type='date'
                    value={start}
                    onValueChanged={onValueChange}
                    displayFormat={display}
                    elementAttr={{class: 'calendarStart'}}
                    openOnFieldClick={true}
                    useMaskBehavior={true}
                    pickerType='rollers'
                    disabled={active === 'setting' ? true : false}
                    max={new Date(now)}
                  />
                  <p className='range'>~</p>
                  <DateBox
                    type='date'
                    value={end}
                    onValueChanged={onValueChange}
                    displayFormat={display}
                    elementAttr={{class: 'calendarEnd'}}
                    openOnFieldClick={true}
                    useMaskBehavior={true}
                    pickerType='rollers'
                    disabled={active === 'setting' ? true : false}
                    max={new Date(now)}
                    dropDownButtonTemplate='calendar'
                  >
                    <Template name='calendar' render={Calendar} />
                  </DateBox>
                </div>
              )
            ) : (
              <DateBox
                type='date'
                value={start}
                onValueChanged={(event) => setStart(event.value)}
                displayFormat={display}
                calendarOptions={{
                  maxZoomLevel: interval === 'monthly' ? 'year' : 'month',
                  minZoomLevel: 'century'
                }}
                openOnFieldClick={true}
                useMaskBehavior={true}
                pickerType={mobile ? 'rollers' : 'calendar'}
                disabled={active === 'setting' ? true : false}
                max={new Date(now)}
                dropDownButtonTemplate='calendar'
                >
                  <Template name='calendar' render={Calendar} />
                </DateBox>
            )}
            { interval !== 'setting' &&
              <DateButton onClick={() => handleDate('next')} disabled={active === 'setting' || !device?.iotDeviceId ? true : false} $next />
            }
          </MobileDateWrapper>
          
          <PrimaryButton type='button' onClick={() => handleSearch()} disabled={active === 'setting' || !device?.iotDeviceId ? true : false}>검색</PrimaryButton>
        </div>

        <div>
          <IconButton type='button' onClick={() => handleDownload()} disabled={!device?.iotDeviceId ? true : false}><Download />다운로드</IconButton>
          <IconButton type='button' onClick={() => setLines(!lines)} className={lines ? 'active' : ''}><Lines />알람 기준선</IconButton>
          <IconButton type='button' id='axisRanges' onClick={() => setVisibleRanges(true)}><Settings />데이터 범위 설정</IconButton>
        </div>
      </SearchWrapper>
        
      <Popover
        target='#setting'
        visible={setting}
        hideOnOutsideClick={true}
        onHiding={() => setSetting(false)}
      >
        <AnalysisPopover>
          <div>
            <InfoMessage>31일 이내 평균 데이터를 조회하실 수 있습니다.</InfoMessage>
            { mobile ? (
              <div className={`rangeBox${detailed ? ' disabled' : ''}`}>
                <DateBox
                  type='date'
                  value={settingDate.start}
                  onValueChanged={(event: any) => setSettingDate({...settingDate, start: event.value})}
                  displayFormat='yyyy-MM-dd'
                  elementAttr={{class: 'calendarStart'}}
                  openOnFieldClick={true}
                  useMaskBehavior={true}
                  pickerType='rollers'
                  disabled={detailed ? true : false}
                  max={new Date(now)}
                />
                <p className='range'>~</p>
                <DateBox
                  type='date'
                  value={settingDate.end}
                  onValueChanged={(event: any) => setSettingDate({...settingDate, end: event.value})}
                  displayFormat='yyyy-MM-dd'
                  elementAttr={{class: 'calendarEnd'}}
                  openOnFieldClick={true}
                  useMaskBehavior={true}
                  pickerType='rollers'
                  disabled={detailed ? true : false}
                  max={new Date(now)}
                  dropDownButtonTemplate='calendar'
                >
                  <Template name='calendar' render={Calendar} />
                </DateBox>
              </div>
            ): (
              <DateRangeBox
                value={[settingDate.start, settingDate.end]}
                onValueChanged={(event: any) => setSettingDate({start: event.value[0], end: event.value[1]})}
                displayFormat='yyyy-MM-dd'
                openOnFieldClick={true}
                useMaskBehavior={true}
                startDateLabel=''
                endDateLabel=''
                disabled={detailed ? true : false}
                max={new Date(now)}
                dropDownButtonTemplate='calendar'
                >
                  <Template name='calendar' render={Calendar} />
                </DateRangeBox>
            )}
          </div>

          <label style={{'display': 'flex', 'alignItems': 'center', 'marginTop': '10px'}}>
            <input type='checkbox' id='checkbox' onChange={(event) => setDetailed(event.target.checked)} />
            <label htmlFor='checkbox' style={{ 'marginRight': '5px' }} />
            상세 검색
          </label>
          
          <div>
            <InfoMessage>일간 상세 데이터를 조회하실 수 있습니다.</InfoMessage>
            <DateBox
              type='date'
              value={detailedDate}
              onValueChanged={(event) => setDetailedDate(event.value)}
              displayFormat='yyyy-MM-dd'
              openOnFieldClick={true}
              useMaskBehavior={true}
              pickerType={mobile ? 'rollers' : 'calendar'}
              disabled={!detailed ? true : false}
              max={new Date(now)}
              dropDownButtonTemplate='calendar'
              >
                <Template name='calendar' render={Calendar} />
              </DateBox>
          </div>

          <PrimaryButton
            type='button'
            onClick={() => {
              handleSearch();
              setSetting(false);
            }}
            style={{ 'color': '#FFFFFF' }}
            disabled={!device?.iotDeviceId ? true : false}
          >
            검색
          </PrimaryButton>
        </AnalysisPopover>
      </Popover>
      <Popover
        target='#axisRanges'
        visible={visibleRanges}
        hideOnOutsideClick={true}
        onHiding={() => {
          setVisibleRanges(false);
          setAxisRange(ranges);
          setFormMessage(false);
        }}
      >
        <RangesPopover>
          <p>데이터 범위 설정</p>
          <div>
            { data.name.map((name: any, index: number) => (
              axisRange &&
              <div key={index}>
                <Label>{ name === 'waterTemperature' ? group.waterTemperature : name }</Label>
                
                <div>
                  <TextSwitch $text={ axisRange[name].manual ? '수동' : '자동' }>
                    <label>
                      <input
                        type='checkbox'
                        checked={axisRange[name].manual}
                        onChange={(event) => setAxisRange({
                          ...axisRange,
                          [name]: {
                            ...axisRange[name],
                            manual: event.target.checked
                          }
                        })}
                      />
                    </label>
                  </TextSwitch>
                  <TextFields
                    type='number'
                    disabled={ axisRange[name].manual ? false : true }
                    value={ axisRange[name].startValue }
                    onChange={(event) => setAxisRange({
                      ...axisRange,
                      [name]: {
                        ...axisRange[name],
                        startValue: Number(event.target.value)
                      }
                    })}
                  />
                  <p>~</p>
                  <TextFields
                    type='number'
                    disabled={ axisRange[name].manual ? false : true }
                    value={ axisRange[name].endValue }
                    onChange={(event) => setAxisRange({
                      ...axisRange,
                      [name]: {
                        ...axisRange[name],
                        endValue: Number(event.target.value)
                      }
                    })}
                  />
                </div>
              </div>
            ))}
            { formMessage &&
              <FormMessage>입력하신 범위를 다시 확인해 주세요.</FormMessage>
            }
            <PrimaryButton type='button' onClick={() => handleRanges()}>저장</PrimaryButton>
          </div>
        </RangesPopover>
      </Popover>

      <Box>
        <RangeSelector
          dataSource={data.datasets}
          onValueChanged={updateVisualRange}
        >
          <Size height={120} />
          <RsChart>
            { data.name.length > 0 && data.name.map((name, index) => (
              <RsSeries key={name} type='line' valueField={name} argumentField='dateTime' color={theme.colors[colors[index]]} />
            ))}
          </RsChart>
          <Behavior valueChangeMode='onHandleMove' />
        </RangeSelector>

        <Chart ref={chartRef} dataSource={data.datasets} width={'100%'} style={{'paddingBottom': '20px'}}>

          <CommonSeriesSettings type={chart === 'line' ? 'line' : 'bar'} argumentField='dateTime' hoverMode='none' barWidth={20} ignoreEmptyPoints={true}>
            <Point visible={false} />
          </CommonSeriesSettings>

          { data.name.length > 0 && data.name.map((name) => (
            <Pane key={name} name={name} height='250' />
          ))}

          { data.name.length > 0 && data.name.map((name, index) => (
            <Series key={name} pane={name} name={name} valueField={name} color={theme.colors[colors[index]]} />
          ))}

          { data.name.length > 0 && data.name.map((name) => (
            <ValueAxis
              showZero={true}
              key={name}
              pane={name}
              valueMarginsEnabled={ ranges[name].manual ? false : true }
              name={name === 'waterTemperature' ? group.waterTemperature : name }
              title={{text: `${name === 'waterTemperature' ? group.waterTemperature : name } (${data.units[name]})`, alignment: 'center', font: {size: '13px', family: 'Pretendard, sans-serif'} }}
              minorTickInterval={0.001}
              maxValueMargin={0.15}
            >
              <VisualRange
                startValue={
                  ranges[name].manual ?
                    ranges[name].startValue
                  : data.alarm[name].alarmType !== 'OFF' && lines ? 
                  Math.min( Math.min(...data.dataArr[name]), data.alarm[name].alarmUpperLimit || 0, 0 )
                  : Math.min( Math.min(...data.dataArr[name]), 0 )
                }
                endValue={
                  ranges[name].manual ?
                    ranges[name].endValue
                  : data.alarm[name].alarmType !== 'OFF' && lines ? 
                  Math.max(Math.max(...data.dataArr[name]), data.alarm[name].alarmUpperLimit || Math.max(...data.dataArr[name]), 10)
                  : Math.max(...data.dataArr[name])
                }
              />
              { data.alarm[name].alarmType !== 'OFF' && data.alarm[name].alarmUpperLimit !== null && lines &&
                <ConstantLine value={Number(data.alarm[name].alarmUpperLimit)} label={{text: '상한', font: {color: '#E62E2E'}}} color='#E62E2E' dashStyle='dash' />
              }
              { data.alarm[name].alarmType !== 'OFF' && data.alarm[name].alarmLowerLimit !== null && lines &&
                <ConstantLine value={Number(data.alarm[name].alarmLowerLimit)} label={{text: '하한', font: {color: '#3361FF'}}} color='#3361FF' dashStyle='dash' />
              }
            </ValueAxis>
          ))}

          <ArgumentAxis visualRange={visualRange} argumentType='datetime'>
            <DxLabel format={customizeText} wordWrap='none' />
          </ArgumentAxis>

          <Tooltip
            enabled={true}
            shared={true}
            contentRender={customizeTooltip}
            border={{visible: false}}
            color='#4D5E80F2'
            arrowLength={0}
            cornerRadius={8}
            shadow={{ offsetX: 0, offsetY: 0, blur: 5, color: '#26334D', opacity: 0.2, family: 'Pretendard, sans-serif' }}
            font={{color: '#FFFFFF'}}
          />

          <Crosshair enabled={true} width={1}>
            <HorizontalLine visible={false} />
          </Crosshair>
          <Legend visible={false} />
        </Chart>
      </Box>

      { message &&
        <Dialog
          handleClose={handleClose}
        >
          <p>{ message }</p>
          
          <PrimaryButton type='button' onClick={() => handleClose()} style={{'marginTop': '40px'}}>확인</PrimaryButton>
        </Dialog>
      }
    </ListContainer>
  );
}